// MARK://dhk8/pos/hooks/useTenantId
// Reactive subscription to the active tenant id.
//
// S1-Y hardening 2026-05-24: ya NO leemos `document.cookie`. Las cookies
// `dhk8_tenant` / `dhk8_user` fueron eliminadas (eran JS-writable y por
// tanto exfiltrables por XSS — el backend ya valida JWT.tenantId vía
// tenantGuard desde S0-2). El tenant activo vive AHORA en memoria, en
// `apps/dhk8-pos/src/lib/api.ts` (`activeTenantOverride`), y se publica
// vía `setActiveTenantId()` desde el auth-store.
//
// Este hook se suscribe directamente a esos cambios — eliminó el polling
// de 500ms al cookie (POLL_MS) que existía pre-S1-Y, y eliminó los
// listeners de `focus`/`visibilitychange` que cubrían el caso de cookie
// flippada en otra pestaña (irrelevante: las pestañas comparten el JWT
// HttpOnly pero NO comparten el state in-memory de api.ts — cada tab
// corre su propio módulo). Si un usuario cambia el env en otro tab, el
// re-render de THIS tab solo ocurre al volver a foco y triggear una
// rehidratación de auth-store; eso es aceptable y consistente con el
// modelo "una sesión POS = una pestaña".
//
// El evento legacy `dhk8:tenant-change` se mantiene como API pública
// (lo emite el auth-store) por compat con tests/consumidores externos
// pero internamente nos suscribimos a setActiveTenantId().

import { useSyncExternalStore } from 'react'
import { getTenantId, subscribeActiveTenantId } from '../lib/api.js'

const TENANT_CHANGE_EVENT = 'dhk8:tenant-change'

type Listener = () => void

let currentValue: string | null = getTenantId()
const listeners = new Set<Listener>()
let unsubscribeFromApi: (() => void) | null = null

function notify(): void {
  for (const fn of listeners) fn()
}

function ensureInstalled(): void {
  if (unsubscribeFromApi !== null) return
  unsubscribeFromApi = subscribeActiveTenantId((next) => {
    if (next !== currentValue) {
      currentValue = next
      notify()
    }
  })
}

function subscribe(listener: Listener): () => void {
  ensureInstalled()
  listeners.add(listener)
  // Defensive resync: el valor pudo haber cambiado entre el module-init y
  // la primera suscripción (p.ej. el auth-store hizo setActiveTenantId
  // antes de que cualquier componente montara este hook).
  const snapshot = getTenantId()
  if (snapshot !== currentValue) {
    currentValue = snapshot
    notify()
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && unsubscribeFromApi !== null) {
      unsubscribeFromApi()
      unsubscribeFromApi = null
    }
  }
}

function getSnapshot(): string | null {
  return currentValue
}

/** Reactive tenant id — re-renders the calling component when the
 *  active tenantId changes (env switch, login, logout). */
export function useTenantId(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Synchronously force-refresh the snapshot (tests). Fires listeners if
 *  el valor in-memory cambió desde el último notify. */
export function refreshTenantId(): void {
  const next = getTenantId()
  if (next !== currentValue) {
    currentValue = next
    notify()
  }
}

/** Emit the legacy `dhk8:tenant-change` event — el auth-store lo emite
 *  como API pública por compat con tests/consumidores externos. La
 *  suscripción interna a setActiveTenantId ya nos cubre. */
export function emitTenantChange(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(TENANT_CHANGE_EVENT))
}

export const __TEST_ONLY = { TENANT_CHANGE_EVENT }
